/* @flow */
import idx from 'idx';
import * as React from 'react';
import { memoize } from 'lodash';
import { graphql } from 'react-apollo';
import { addSequenceItemsToQueryResult } from '../utils/query';
import GET_ANNOUNCEMENT_STREAM_ITEMS from '../queries/GetAnnouncementStreamItems.gql';
import type {
  GetAnnouncementStreamItems as GetAnnouncementStreamItemsData,
  GetAnnouncementStreamItems_entityStreams,
  GetAnnouncementStreamItems_entityStreams_sequenceItems,
} from '../queries/__generated__/GetAnnouncementStreamItems';
import type { ApolloError } from 'apollo-server-errors';

export const FIRST_PAGE_VAR = {
  gapItemNextPageParams: '{"limit":20,"since_seq":0}',
};

export type AnnouncementStreamContainerChildrenProps = {
  loadGaps: (gaps: Array<GetAnnouncementStreamItems_entityStreams_sequenceItems>) => void,
  error: ApolloError,
  stream: GetAnnouncementStreamItems_entityStreams | null,
  loading: boolean,
  hasItems: boolean,
};

type Props = {
  data: ReactApolloData<GetAnnouncementStreamItemsData>,
  uuid: string,
  children: (childProps: AnnouncementStreamContainerChildrenProps) => React.Element,
  onNewBottomItem: () => void,
};

class AnnouncementStreamContainer extends React.Component<Props, void> {
  componentDidUpdate(prevProps: Props) {
    const curr = this.getSequenceItems(this.props);
    const prev = this.getSequenceItems(prevProps);

    if (curr[0] !== prev[0]) {
      this.props.onNewBottomItem();
    }
  }

  isLoading = (props: Props): boolean => {
    return props.data.loading || props.data.networkStatus < 7;
  };

  getSequenceItems = (
    props: Props
  ): Array<GetAnnouncementStreamItems_entityStreams_sequenceItems> => {
    const stream = this.getStream(props);

    if (stream && stream.uuid === this.props.uuid) {
      return stream.sequenceItems ? [...stream.sequenceItems] : [];
    } else {
      return [];
    }
  };

  getStream = (props: Props): GetAnnouncementStreamItems_entityStreams | null => {
    const stream = idx(props, _ => _.data.entityStreams[0]);

    if (stream && stream.uuid === this.props.uuid) {
      return stream;
    } else {
      return null;
    }
  };

  loadGaps = (gaps: Array<GetAnnouncementStreamItems_entityStreams_sequenceItems>) => {
    for (const gap of gaps) {
      this.loadGap(gap);
    }
  };

  // memoize this function to avoid reloading a gap multiple time
  // when switching between chats.
  loadGap = memoize<Array<GetAnnouncementStreamItems_entityStreams_sequenceItems>, void>(
    (gap: GetAnnouncementStreamItems_entityStreams_sequenceItems) => {
      const { uuid } = this.props;
      const gapItemNextPageParams =
        gap.item.__typename === 'GapItem' ? gap.item.nextPageParams : undefined;

      this.props.data
        .fetchMore({
          variables: { uuid, gapItemNextPageParams },
          updateQuery: (previous, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
              return previous;
            }

            return addSequenceItemsToQueryResult(
              previous,
              fetchMoreResult.entityStreams[0].sequenceItems
            );
          },
        })
        .catch(e => console.error('could not load announcement gap message: ', e));
    }
  );

  render() {
    const sequenceItems = this.getSequenceItems(this.props);
    return this.props.children({
      loadGaps: this.loadGaps,
      error: this.props.data.error,
      stream: this.getStream(this.props),
      loading: this.isLoading(this.props),
      hasItems: sequenceItems.length > 0,
    });
  }
}

const WithMessages = graphql(GET_ANNOUNCEMENT_STREAM_ITEMS, {
  options: ({ uuid }) => ({
    notifyOnNetworkStatusChange: true,
    variables: {
      uuid,
      ...FIRST_PAGE_VAR,
    },
  }),
});

export { AnnouncementStreamContainer };
export default WithMessages(AnnouncementStreamContainer);
